import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faPlus, faEdit, faTrash, faShieldAlt, faCheck, faCoins, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const PaymentSettings: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const { business } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2025',
      isDefault: true,
      holderName: 'Ahmet Yılmaz'
    },
    {
      id: 2,
      type: 'mastercard',
      last4: '5555',
      expiryMonth: '08',
      expiryYear: '2026',
      isDefault: false,
      holderName: 'Ahmet Yılmaz'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    holderName: '',
    isDefault: false
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.dropdown-container')) {
          setOpenDropdownId(null);
        }
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleAddCard = (e: React.FormEvent) => {
    e.preventDefault();
    const newPaymentMethod = {
      id: Date.now(),
      type: newCard.cardNumber.startsWith('4') ? 'visa' : 'mastercard',
      last4: newCard.cardNumber.slice(-4),
      expiryMonth: newCard.expiryMonth,
      expiryYear: newCard.expiryYear,
      isDefault: newCard.isDefault,
      holderName: newCard.holderName
    };

    setPaymentMethods([...paymentMethods, newPaymentMethod]);
    setNewCard({
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      holderName: '',
      isDefault: false
    });
    setShowAddForm(false);
  };

  const setDefaultCard = (id: number) => {
    setPaymentMethods(paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
  };

  const deleteCard = (id: number) => {
    setPaymentMethods(paymentMethods.filter(method => method.id !== id));
  };

  const getCardIcon = (type: string) => {
    return type === 'visa' ? '💳' : '💳';
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('paymentSettings.title')}</h2>
        <p className="text-white/70">{t('paymentSettings.description')}</p>
      </div>

      <div className="mb-8 bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2 flex items-center space-x-2">
              <FontAwesomeIcon icon={faCoins} className="text-purple-400" />
              <span>{t('paymentSettings.creditManagement')}</span>
            </h3>
            <p className="text-white/70 text-sm">
              {t('paymentSettings.creditManagementDesc')}
            </p>
            {business && (
              <p className="text-white/50 text-xs mt-2">
                {t('paymentSettings.businessAccount')}: {business.name || business.email}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate(`/${lang || 'tr'}/add-credit`)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 rounded-lg text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden md:inline">{t('paymentSettings.addCredit')}</span>
          </button>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">{t('paymentSettings.savedPaymentMethods')}</h3>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-4 py-2 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 transform hover:scale-105"
          >
            <FontAwesomeIcon icon={faPlus} className="text-sm" />
            <span className="hidden md:inline">{t('paymentSettings.addNewCard')}</span>
          </button>
        </div>

        <div className="space-y-4 overflow-visible">
          {paymentMethods.map((method, index) => (
            <div key={method.id} className={`backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl px-6 ${method.isDefault ? 'pb-6 pt-1' : 'py-6'} relative overflow-visible z-[${paymentMethods.length - index}]`}>
              {method.isDefault && (
                <span className="px-2.5 sm:px-2 bg-green-500/20 text-green-400 text-[10px] sm:text-xs rounded-full border border-green-400/30 whitespace-nowrap flex-shrink-0 ml-[-14px]">
                  {t('paymentSettings.default')}
                </span>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                    <span className="text-white text-lg">{getCardIcon(method.type)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <h4 className="text-white font-semibold text-sm sm:text-base">
                        {method.type.toUpperCase()} •••• {method.last4}
                      </h4>
                    </div>
                    <p className="text-white/60 text-xs sm:text-sm mt-1">
                      {method.holderName} • {method.expiryMonth}/{method.expiryYear}
                    </p>
                  </div>
                </div>
                <div className="relative dropdown-container z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdownId(openDropdownId === method.id ? null : method.id);
                    }}
                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} className="text-sm" />
                  </button>
                  {openDropdownId === method.id && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900/95 border border-white/20 rounded-xl shadow-2xl z-[100] overflow-hidden">
                      {!method.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDefaultCard(method.id);
                            setOpenDropdownId(null);
                          }}
                          className="w-full px-4 py-3 text-left text-white/80 hover:text-white hover:bg-white/20 transition-colors text-sm flex items-center space-x-2"
                        >
                          <FontAwesomeIcon icon={faCheck} className="text-sm" />
                          <span>{t('paymentSettings.setAsDefault')}</span>
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(null);
                          // Edit functionality can be added here
                        }}
                        className="w-full px-4 py-3 text-left text-white/80 hover:text-white hover:bg-white/20 transition-colors text-sm flex items-center space-x-2"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                        <span>{t('paymentSettings.edit')}</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCard(method.id);
                          setOpenDropdownId(null);
                        }}
                        className="w-full px-4 py-3 text-left text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors text-sm flex items-center space-x-2"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                        <span>{t('paymentSettings.delete')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showAddForm && (
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">{t('paymentSettings.addNewCard')}</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-white/60 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleAddCard} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="cardNumber" className="block text-sm font-medium text-white/80 mb-2">
                  {t('paymentSettings.cardNumber')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faCreditCard} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="text"
                    id="cardNumber"
                    value={newCard.cardNumber}
                    onChange={(e) => setNewCard({ ...newCard, cardNumber: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="holderName" className="block text-sm font-medium text-white/80 mb-2">
                  {t('paymentSettings.cardHolderName')}
                </label>
                <input
                  type="text"
                  id="holderName"
                  value={newCard.holderName}
                  onChange={(e) => setNewCard({ ...newCard, holderName: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  placeholder="Ahmet Yılmaz"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="expiryMonth" className="block text-sm font-medium text-white/80 mb-2">
                    {t('paymentSettings.expireMonth')}
                  </label>
                  <select
                    id="expiryMonth"
                    value={newCard.expiryMonth}
                    onChange={(e) => setNewCard({ ...newCard, expiryMonth: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    required
                  >
                    <option value="">{t('paymentSettings.expireMonth')}</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                        {String(i + 1).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="expiryYear" className="block text-sm font-medium text-white/80 mb-2">
                    {t('paymentSettings.expireYear')}
                  </label>
                  <select
                    id="expiryYear"
                    value={newCard.expiryYear}
                    onChange={(e) => setNewCard({ ...newCard, expiryYear: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    required
                  >
                    <option value="">{t('paymentSettings.expireYear')}</option>
                    {Array.from({ length: 10 }, (_, i) => (
                      <option key={i} value={String(new Date().getFullYear() + i)}>
                        {new Date().getFullYear() + i}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="cvv" className="block text-sm font-medium text-white/80 mb-2">
                  {t('paymentSettings.cvv')}
                </label>
                <input
                  type="text"
                  id="cvv"
                  value={newCard.cvv}
                  onChange={(e) => setNewCard({ ...newCard, cvv: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  placeholder="123"
                  maxLength={4}
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isDefault"
                checked={newCard.isDefault}
                onChange={(e) => setNewCard({ ...newCard, isDefault: e.target.checked })}
                className="w-4 h-4 text-purple-600 bg-white/5 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
              />
              <label htmlFor="isDefault" className="text-white/70 text-sm">
                {t('paymentSettings.setAsDefaultPayment')}
              </label>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors"
              >
                {t('paymentSettings.cancel')}
              </button>
              <button
                type="submit"
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-3 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 transform hover:scale-105"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span>{t('paymentSettings.addCard')}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-400/20 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <FontAwesomeIcon icon={faShieldAlt} className="text-green-400 text-lg mt-1" />
          <div>
            <h4 className="text-lg font-semibold text-white mb-2">{t('paymentSettings.securePayment')}</h4>
            <p className="text-white/70 text-sm mb-3">
              {t('paymentSettings.securePaymentDesc')}
            </p>
            <div className="flex flex-col md:flex-row items-start md:items-center space-x-0 md:space-x-4 space-y-2 md:space-y-0 text-xs md:text-sm text-white/60">
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                <span>{t('paymentSettings.sslEncryption')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                <span>{t('paymentSettings.pciCompliant')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                <span>{t('paymentSettings.secure3D')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
